import { loadStripe } from '@stripe/stripe-js';
import { STRIPE_CONFIG, validateStripeConfig } from '../config/stripe';

let stripePromise = null;

// Initialize Stripe
export const getStripe = () => {
  // Validate configuration first
  if (!validateStripeConfig()) {
    throw new Error('Stripe configuration is invalid. Please check the console for details.');
  }

  if (!stripePromise) {
    console.log('Initializing Stripe with key:', STRIPE_CONFIG.publishableKey);
    
    try {
      stripePromise = loadStripe(STRIPE_CONFIG.publishableKey);
      console.log('Stripe promise created successfully');
    } catch (error) {
      console.error('Error creating Stripe promise:', error);
      throw error;
    }
  }
  return stripePromise;
};

// Create payment intent for service provider upgrade
export const createPaymentIntent = async (amount, description, metadata = {}) => {
  try {
    const response = await fetch('/api/create-payment-intent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount,
        description,
        metadata,
        currency: STRIPE_CONFIG.currency,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create payment intent');
    }

    const data = await response.json();
    return data.clientSecret;
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw new Error('Payment setup failed. Please try again.');
  }
};

// Create setup intent for property owner trial (no charge, just save payment method)
export const createSetupIntent = async (metadata = {}) => {
  try {
    const response = await fetch('/api/create-setup-intent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        metadata,
        currency: STRIPE_CONFIG.currency,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create setup intent');
    }

    if (!response.ok) {
      throw new Error('Failed to create setup intent');
    }

    const data = await response.json();
    return data.clientSecret;
  } catch (error) {
    console.error('Error creating setup intent:', error);
    throw new Error('Payment setup failed. Please try again.');
  }
};

// Confirm payment for service provider upgrade
export const confirmPayment = async (clientSecret, paymentMethod) => {
  try {
    const stripe = await getStripe();
    const { error, paymentIntent } = await stripe.confirmPayment({
      clientSecret,
      payment_method: paymentMethod,
      return_url: STRIPE_CONFIG.successUrl,
    });

    if (error) {
      throw new Error(error.message);
    }

    return paymentIntent;
  } catch (error) {
    console.error('Payment confirmation error:', error);
    throw error;
  }
};

// Confirm setup intent for property owner trial
export const confirmSetupIntent = async (clientSecret, paymentMethod) => {
  try {
    const stripe = await getStripe();
    const { error, setupIntent } = await stripe.confirmSetup({
      clientSecret,
      payment_method: paymentMethod,
      return_url: STRIPE_CONFIG.successUrl,
    });

    if (error) {
      throw new Error(error.message);
    }

    return setupIntent;
  } catch (error) {
    console.error('Setup confirmation error:', error);
    throw error;
  }
};

// Get payment method details
export const getPaymentMethod = async (paymentMethodId) => {
  try {
    const response = await fetch(`/api/payment-methods/${paymentMethodId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get payment method');
    }

    const data = await response.json();
    return data.paymentMethod;
  } catch (error) {
    console.error('Error getting payment method:', error);
    throw error;
  }
};

// Update payment method
export const updatePaymentMethod = async (paymentMethodId, updates) => {
  try {
    const response = await fetch(`/api/payment-methods/${paymentMethodId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      throw new Error('Failed to update payment method');
    }

    const data = await response.json();
    return data.paymentMethod;
  } catch (error) {
    console.error('Error updating payment method:', error);
    throw error;
  }
};

// Delete payment method
export const deletePaymentMethod = async (paymentMethodId) => {
  try {
    const response = await fetch(`/api/payment-methods/${paymentMethodId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to delete payment method');
    }

    return true;
  } catch (error) {
    console.error('Error deleting payment method:', error);
    throw error;
  }
};

// Get payment history
export const getPaymentHistory = async (userId) => {
  try {
    const response = await fetch(`/api/payments/${userId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get payment history');
    }

    const data = await response.json();
    return data.payments;
  } catch (error) {
    console.error('Error getting payment history:', error);
    throw error;
  }
};

// Create subscription for property owner (after trial)
export const createSubscription = async (priceId, customerId, paymentMethodId) => {
  try {
    const response = await fetch('/api/subscriptions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        priceId,
        customerId,
        paymentMethodId,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create subscription');
    }

    const data = await response.json();
    return data.subscription;
  } catch (error) {
    console.error('Error creating subscription:', error);
    throw error;
  }
};

// Cancel subscription
export const cancelSubscription = async (subscriptionId) => {
  try {
    const response = await fetch(`/api/subscriptions/${subscriptionId}/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to cancel subscription');
    }

    const data = await response.json();
    return data.subscription;
  } catch (error) {
    console.error('Error canceling subscription:', error);
    throw error;
  }
};
